import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../config";
import { normalizeMediaUrl } from "../utils/normalizeMediaUrl";
import PatientSidebarShell from "./PatientSidebarShell";

const PatientProfileView = () => {
  const navigate = useNavigate();
  const patientID = localStorage.getItem("patientID");

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${config.API_BASE_URL}${config.PATIENT.PROFILE}/${patientID}`)
      .then((res) => setProfile(res.data))
      .catch(() => navigate("/patientProfileEdit"))
      .finally(() => setLoading(false));
  }, [patientID, navigate]);

  if (loading) return <p style={{ padding: 40 }}>Loading profile...</p>;
  if (!profile) return <p style={{ padding: 40 }}>Profile not found</p>;

  const selfRegistered = profile.selfRegistered || {};
  const normalizedPictureUrl = normalizeMediaUrl(selfRegistered.pictureUrl);

  return (
    <PatientSidebarShell activeItem="profile">
      <div style={card}>
        <div style={headerActionRow}>
          <h2 style={title}>Resident Profile</h2>
          <button style={editBtnSmall} onClick={() => navigate("/patientProfileEdit")}>
            Edit Profile
          </button>
        </div>

        <div style={gridContainer}>
          <div style={leftCol}>
            <div style={profileHeader}>
              <div style={imageContainer}>
                {normalizedPictureUrl ? (
                  <img
                    src={normalizedPictureUrl}
                    alt="Resident profile"
                    style={imagePreview}
                  />
                ) : (
                  <div style={imagePlaceholder}>
                    <span style={imagePlaceholderText}>
                      {profile.firstName ? profile.firstName[0].toUpperCase() : "?"}
                    </span>
                  </div>
                )}
              </div>
              <div>
                <h1 style={profileName}>
                  {profile.firstName} {profile.lastName}
                </h1>
                <p style={profileSub}>
                  {profile.city ? `${profile.city}, ` : ""}
                  {profile.country || "Location not set"}
                </p>
              </div>
            </div>

            <Section title="Basic Details">
              <Row label="Gender" value={profile.gender === "M" ? "Male" : profile.gender === "F" ? "Female" : profile.gender} />
              <Row label="Age" value={selfRegistered.age} />
              <Row label="Spoken Language" value={profile.spokenLanguage} />
              <Row label="Race" value={selfRegistered.race} />
              <Row label="Marital Status" value={selfRegistered.maritalStatus} />
              <Row label="Education" value={selfRegistered.education} />
            </Section>
          </div>

          <div style={rightCol}>
            <Section title="Contact Information">
              <Row label="WhatsApp" value={profile.whatsapp} />
              <Row label="Family Contact Name" value={selfRegistered.familyContactName} />
              <Row label="Family Contact Phone" value={selfRegistered.familyContactPhone} />
              <Row label="Family Contact Email" value={selfRegistered.familyContactEmail} />
            </Section>

            <Section title="Health & Physical">
              <div style={twoColRow}>
                <Row label="Weight (kg)" value={selfRegistered.weightKg} />
                <Row label="Height (cm)" value={selfRegistered.heightCm} />
              </div>
              
              <Row
                label="Medications"
                value={
                  Array.isArray(selfRegistered.medications) && selfRegistered.medications.length > 0
                    ? selfRegistered.medications.join(", ")
                    : "None recorded"
                }
              />
              <Row
                label="Chronic Conditions"
                value={
                  Array.isArray(selfRegistered.chronicConditions) && selfRegistered.chronicConditions.length > 0
                    ? selfRegistered.chronicConditions.join(", ")
                    : "None recorded"
                }
              />
            </Section>
          </div>
        </div>

        {(selfRegistered.notesContext || selfRegistered.notesEmotional || selfRegistered.notesPhysical) && (
          <div style={fullWidthSection}>
            <Section title="Context & Notes">
              <Row label="General Context" value={selfRegistered.notesContext} multiline />
              <Row label="Emotional Notes" value={selfRegistered.notesEmotional} multiline />
              <Row label="Physical Notes" value={selfRegistered.notesPhysical} multiline />
            </Section>
          </div>
        )}

        <div style={actionRow}>
          <button style={secondaryBtn} onClick={() => navigate("/user-home")}>
            Back to Home
          </button>
        </div>
      </div>
    </PatientSidebarShell>
  );
};

export default PatientProfileView;

const Section = ({ title, children }) => {
  let hasContent = false;
  // Simple check to see if we have valid children values to render
  if (Array.isArray(children)) {
    hasContent = children.some(child => {
      if (!child) return false;
      if (child.props && child.props.value) return true;
      if (child.props && child.props.children) return true;
      return false;
    });
  } else if (children && children.props) {
    hasContent = !!children.props.value || !!children.props.children;
  }

  return (
    <div style={section}>
      <h3 style={sectionTitle}>{title}</h3>
      <div style={sectionContent}>
        {children}
        {!hasContent && <div style={emptyText}>No information provided</div>}
      </div>
    </div>
  );
};

const Row = ({ label, value, multiline = false }) => {
  if (value === undefined || value === null || value === "") return null;

  return (
    <div style={rowContainer}>
      <div style={rowLabel}>{label}</div>
      <div style={multiline ? rowValueMultiline : rowValue}>
        {String(value)}
      </div>
    </div>
  );
};

const card = {
  background: "#fff",
  padding: "32px",
  width: "100%",
  maxWidth: "900px",
  borderRadius: "12px",
  boxShadow: "0 4px 16px rgba(0, 0, 0, 0.06)",
  margin: "0 auto",
  boxSizing: "border-box"
};

const headerActionRow = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "30px",
  borderBottom: "1px solid #edf2f7",
  paddingBottom: "16px"
};

const title = { 
  color: "#0E5580", 
  margin: 0,
  fontSize: "24px",
  fontWeight: 600
};

const editBtnSmall = {
  padding: "8px 16px",
  background: "#ffffff",
  color: "#0E5580",
  border: "1px solid #0E5580",
  borderRadius: "8px",
  cursor: "pointer",
  fontSize: "14px",
  transition: "all 0.2s ease"
};

const gridContainer = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
  gap: "40px",
  marginBottom: "20px"
};

const leftCol = {
  display: "flex",
  flexDirection: "column",
  gap: "24px"
};

const rightCol = {
  display: "flex",
  flexDirection: "column",
  gap: "24px"
};

const profileHeader = {
  display: "flex",
  alignItems: "center",
  gap: "20px",
  marginBottom: "10px"
};

const imageContainer = {
  flexShrink: 0
};

const imagePreview = {
  width: "100px",
  height: "100px",
  objectFit: "cover",
  borderRadius: "50%",
  border: "2px solid #eef3f7"
};

const imagePlaceholder = {
  width: "100px",
  height: "100px",
  borderRadius: "50%",
  background: "#0E5580",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
};

const imagePlaceholderText = {
  color: "#fff",
  fontSize: "40px",
  fontWeight: "bold"
};

const profileName = {
  margin: "0 0 4px 0",
  color: "#12344d",
  fontSize: "24px",
  fontWeight: 600
};

const profileSub = {
  margin: 0,
  color: "#6b8396",
  fontSize: "15px"
};

const section = { 
  background: "#f9fcff",
  borderRadius: "12px",
  border: "1px solid #eef3f7",
  padding: "20px",
  height: "100%",
  boxSizing: "border-box"
};

const sectionTitle = { 
  color: "#0E5580", 
  fontSize: "18px",
  fontWeight: 600,
  margin: "0 0 16px 0",
  paddingBottom: "8px",
  borderBottom: "1px solid #e1ebf2"
};

const sectionContent = {
  display: "flex",
  flexDirection: "column",
  gap: "14px"
};

const twoColRow = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "16px"
};

const fullWidthSection = {
  marginTop: "20px",
  marginBottom: "20px"
};

const rowContainer = {
  display: "flex",
  flexDirection: "column",
  gap: "4px"
};

const rowLabel = { 
  fontSize: "14px", 
  color: "#4f6475",
  fontWeight: 600
};

const rowValue = { 
  fontSize: "15px",
  color: "#234159",
  fontWeight: 500
};

const rowValueMultiline = { 
  fontSize: "15px",
  color: "#234159",
  fontWeight: 400,
  whiteSpace: "pre-wrap",
  lineHeight: 1.5,
  background: "#fff",
  padding: "12px",
  borderRadius: "8px",
  border: "1px solid #e6eff5",
  marginTop: "4px"
};

const emptyText = {
  color: "#9fb3c2",
  fontSize: "14px",
  fontStyle: "italic"
};

const actionRow = {
  display: "flex",
  justifyContent: "flex-end",
  marginTop: "32px",
  paddingTop: "20px",
  borderTop: "1px solid #edf2f7"
};

const secondaryBtn = {
  padding: "12px 24px",
  background: "#ffffff",
  color: "#0E5580",
  border: "1px solid #0E5580",
  borderRadius: "8px",
  cursor: "pointer",
  fontSize: "15px",
  transition: "all 0.2s ease"
};
